/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { DetailsRow, IColumn, IDetailsRowProps, IDetailsRowStyles } from '@fluentui/react/lib/DetailsList';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { SublistEntityCapability } from 'src/core/types/EntityStore';
import { SublistSynonymsCell } from 'src/luis/verticals/entityDetails/components/listEntityDetails/SublistSynonymsCell';

export type CluEntitySublistInEditModeProps = {
    columns: IColumn[];
    onDismiss: () => void;
    projectCulture?: string;
    rowProps?: IDetailsRowProps;
    projectType?: CluProjectType;
    initialSublist?: SublistEntityCapability;
    onSubmit: (sublist: SublistEntityCapability) => void;
};

const messages = defineMessages({
    save: { id: 'clu.cluEntitySublistInEditMode.save', defaultMessage: 'Save' },
    cancel: { id: 'clu.cluEntitySublistInEditMode.cancel', defaultMessage: 'Cancel' },
    addLanguage: { id: 'clu.cluEntitySublistInEditMode.addLanguage', defaultMessage: 'Add language' },
    languagePlaceholder: { id: 'clu.cluEntitySublistInEditMode.languagePlaceholder', defaultMessage: 'Language' },
    normalizedValuePlaceholder: { id: 'clu.cluEntitySublistInEditMode.normalizedValuePlaceholder', defaultMessage: 'Enter value' },
    languageDropDownAriaLabel: { id: 'clu.cluEntitySublistInEditMode.languageDropDownAriaLabel', defaultMessage: 'Languages dropDown' },
    synonymsPlaceholder: { id: 'clu.cluEntitySublistInEditMode.synonymsPlaceholder', defaultMessage: 'Add synonyms separated by commas' }
});

const { styleComponent } = getStylistV2('CluEntitySublistInEditMode');

const scopedTheme = getScopedTheme('CluEntitySublistInEditMode');

const RowContainer = styleComponent<IStackProps>(Stack)(
    'RowContainer',
    (_, cssVars) => ({
        backgroundColor: cssVars.defaultBackground,
        borderBottom: `${scopedTheme.focussingColor} solid 1px`,
        $nest: { '&:hover': { backgroundColor: cssVars.hoveringBackground } }
    }),
    {
        cssVars: { hoveringBackground: '', defaultBackground: '' },
        cssVarsSetter: ({ isSelected, isNewSublist }: { isSelected: boolean; isNewSublist: boolean }) => ({
            hoveringBackground: `${isSelected ? scopedTheme.focussingColor : scopedTheme.hoveringColor}`,
            defaultBackground: `${isNewSublist ? scopedTheme.focussingColor : isSelected ? scopedTheme.hoveringColor : 'unset'}`
        })
    }
);

const detailsRowStyles = classNamesFunction<IDetailsRowProps, IDetailsRowStyles>()({
    cell: { overflow: 'visible' },
    root: { borderBottom: 'none', backgroundColor: 'transparent !important' }
});

/**
 * A component managing and displaying the sublist row in both edit and add modes.
 */
export const CluEntitySublistInEditMode = observer((props: CluEntitySublistInEditModeProps) => {
    const { columns, initialSublist, onSubmit, onDismiss, rowProps, projectType, projectCulture = '' } = props;

    const defaultSublist: SublistEntityCapability = { id: '', listKey: '', synonyms: [{ language: projectCulture, values: [] }] };

    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();

    const [hasSublistDataBeenModified, setHasSublistDataBeenModified] = useState<boolean>(false);
    const [sublist, updateSublist] = useUpdateState<SublistEntityCapability>(initialSublist ?? defaultSublist);

    const isSelected = rowProps?.selection.getSelectedIndices().includes(rowProps?.itemIndex);

    const isNewSublist = !initialSublist;
    const lastSynonym = sublist.synonyms[sublist.synonyms.length - 1];
    const isLastSynonymIncomplete = !lastSynonym.language || !lastSynonym.values[0];
    const isSaveButtonDisabled =
        !sublist.listKey ||
        !sublist.synonyms[0].language ||
        !sublist.synonyms[0].values[0] ||
        !(isNewSublist || hasSublistDataBeenModified);

    const languageOptions: IDropdownOption[] = (projectType === 'Conversation'
        ? rootStore.cluConversationCultureStoreHouse
        : rootStore.cluOrchestrationCultureStoreHouse
    ).items.map(l => ({
        key: l.code,
        text: l.name,
        disabled: Boolean(sublist.synonyms.find(s => s.language === l.code))
    }));

    const onSaveSublist = () => {
        const validatedSublist: SublistEntityCapability = {
            ...sublist,
            synonyms: (sublist.synonyms.reduce((acc, synonym) => acc && synonym.values.every(v => v !== sublist.listKey), true)
                ? sublist.synonyms.map((s, sIndex) => (sIndex === 0 ? { ...s, values: [sublist.listKey, ...s.values] } : s))
                : sublist.synonyms
            ).map(synonym => ({
                ...synonym,
                values: synonym.values.map(value => value.trim()).filter(value => value)
            }))
        };

        if (isLastSynonymIncomplete) {
            validatedSublist.synonyms.pop();
        }

        onSubmit(validatedSublist);
    };

    const onSynonymsChange = (synonymsListIndex: number, newValues: string[], isDeletion: boolean) => {
        updateSublist({
            synonyms: sublist.synonyms.map((s, idx) =>
                idx === synonymsListIndex
                    ? {
                          language: s.language,
                          values: isDeletion ? s.values.filter(v => !newValues.some(value => value === v)) : [...s.values, ...newValues]
                      }
                    : s
            )
        });
        setHasSublistDataBeenModified(true);
    };

    const getColumnContent = (columnKey: string): (() => JSX.Element) => {
        switch (columnKey) {
            case 'listKey':
                return () => (
                    <LuisTextField
                        value={sublist.listKey}
                        key={`${sublist.id}-${columnKey}`}
                        placeholder={localize(messages.normalizedValuePlaceholder)}
                        onChange={(_, canonicalForm) => {
                            setHasSublistDataBeenModified(true);
                            updateSublist({ listKey: canonicalForm });
                        }}
                    />
                );
            case 'language':
                return () => (
                    <Stack tokens={{ childrenGap: 12 }}>
                        <Stack tokens={{ childrenGap: 8 }}>
                            {sublist.synonyms.map((synonym, index) => (
                                <LuisDropdown
                                    required
                                    options={languageOptions}
                                    selectedKey={synonym.language}
                                    key={`${sublist.id}-${columnKey}-${index}`}
                                    placeholder={localize(messages.languagePlaceholder)}
                                    ariaLabel={localize(messages.languageDropDownAriaLabel)}
                                    onChange={(_, option) => {
                                        updateSublist({
                                            synonyms: sublist.synonyms.map((s, idx) =>
                                                idx === index ? { language: option.key as string, values: s.values } : s
                                            )
                                        });
                                        setHasSublistDataBeenModified(true);
                                    }}
                                />
                            ))}
                        </Stack>

                        <ActionButton
                            iconProps={{ iconName: 'Add' }}
                            disabled={isLastSynonymIncomplete}
                            text={localize(messages.addLanguage)}
                            onClick={() => updateSublist({ synonyms: sublist.synonyms.concat([{ language: projectCulture, values: [] }]) })}
                        />
                    </Stack>
                );
            case 'synonyms':
                return () => (
                    <Stack verticalFill>
                        {sublist.synonyms.map((synonym, index) => (
                            <SublistSynonymsCell
                                isEditMode
                                values={synonym.values}
                                onAddValues={values => onSynonymsChange(index, values, false)}
                                onRemoveValues={values => onSynonymsChange(index, values, true)}
                            />
                        ))}
                    </Stack>
                );
            default:
                return null;
        }
    };

    return (
        <RowContainer key={`sublist-${sublist.id}`} customProps={{ isSelected, isNewSublist }}>
            {isNewSublist ? (
                <Stack horizontal grow tokens={{ padding: '0 0 0 48px' }}>
                    {columns.map(column => (
                        <Stack.Item
                            key={column.key}
                            styles={{
                                root: { width: column.calculatedWidth, padding: '11px 8px 11px 12px' }
                            }}>
                            {getColumnContent(column.key)()}
                        </Stack.Item>
                    ))}
                </Stack>
            ) : (
                <DetailsRow
                    {...rowProps}
                    styles={detailsRowStyles}
                    columns={columns.map(c => ({ ...c, onRender: getColumnContent(c.key) }))}
                />
            )}

            <Stack horizontal verticalAlign="center" horizontalAlign="end" tokens={{ childrenGap: 4, padding: 8 }}>
                <PrimaryButton text={localize(messages.save)} disabled={isSaveButtonDisabled} onClick={onSaveSublist} />
                <DefaultButton text={localize(messages.cancel)} onClick={onDismiss} />
            </Stack>
        </RowContainer>
    );
});
